import { useEffect, useMemo, useRef } from "react";
import { philosopher } from "@/lib/fonts";
import ProductCard from "@/app/components/product/ProductCard";
import { ProductCardSkeleton } from "@/app/components/product/ProductListingSkeleton";
import { homeDivider } from "@/assets";
import { useShowInitialLoader } from "@/lib/hooks";
import { useRtkInfiniteList } from "@/lib/hooks/useRtkInfiniteList";
import { useGetCollectionsQuery } from "@/lib/api/apiSlice";
import { useSession } from "@/contexts/AuthContext";
import {
  ICollection,
  IProductVariant,
  ProductStatus,
  defaultPaginationOptions,
  endpoints,
} from "muminsmart-utils";

function CollectionsSkeleton() {
  return (
    <section className="px-4 py-3">
      <div className="mb-4 h-8 w-40 animate-pulse rounded bg-[#F0E0C7]/60" />
      <div className="flex gap-4 overflow-hidden">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={index} className="w-40 shrink-0">
            <ProductCardSkeleton />
          </div>
        ))}
      </div>
    </section>
  );
}

const SingleCollection = ({ collection }: { collection: ICollection }) => {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const baseParams = useMemo(
    () => ({
      ...defaultPaginationOptions,
      sortBy: "createdAt",
      sortOrder: "DESC",
      relations: "product,variantAttributes",
      isActive: true,
      status: ProductStatus.Verified,
    }),
    [],
  );

  const {
    items: variants,
    page,
    hasMore,
    isInitialLoading,
    isLoadingMore,
    loadPage,
  } = useRtkInfiniteList<IProductVariant>({
    endpoint: endpoints.collection.productVariantsPaginated(collection.id),
    baseParams,
    errorMessage: "Failed to fetch collection products",
  });

  useEffect(() => {
    void loadPage(1, true);
  }, [loadPage]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = scrollContainerRef.current;
    if (!node || !root) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          void loadPage(page + 1);
        }
      },
      { root, threshold: 0.1 },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, loadPage, page]);

  if (!isInitialLoading && variants.length === 0) {
    return null;
  }

  return (
    <section>
      <img
        src={homeDivider}
        alt="Group"
        width={1000}
        height={1000}
        className="h-auto w-full px-4 py-3"
      />
      <div className="flex items-center justify-between bg-white px-4 py-3">
        <h2
          className={`flex items-center gap-2 text-2xl font-semibold ${philosopher.className}`}
        >
          {collection.name}
          <span className="rounded-full bg-orange-400 px-2 py-0.5 text-xs text-white">
            Deals
          </span>
        </h2>
      </div>

      <div className="space-y-2 bg-white px-4 py-3">
        {isInitialLoading ? (
          <div className="flex gap-4 overflow-hidden pb-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-40 shrink-0">
                <ProductCardSkeleton />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={scrollContainerRef}
            className="flex gap-4 overflow-x-auto pb-2"
          >
            {variants.map((variant) => (
              <div key={variant.id} className="w-40 shrink-0">
                <ProductCard variant={variant} product={variant.product} />
              </div>
            ))}
            {hasMore ? (
              <div
                ref={sentinelRef}
                className="w-1 shrink-0"
                aria-hidden="true"
              />
            ) : null}
            {isLoadingMore ? (
              <div className="w-40 shrink-0">
                <ProductCardSkeleton />
              </div>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
};

const HomeCollections = () => {
  const { status } = useSession();
  const { data: collections, isLoading: isCollectionsLoading } =
    useGetCollectionsQuery(undefined, {
      skip: status === "loading",
    });

  const showCollectionsLoader = useShowInitialLoader(
    isCollectionsLoading,
    collections,
  );

  if (showCollectionsLoader) {
    return <CollectionsSkeleton />;
  }

  if (!collections?.length) {
    return null;
  }

  return (
    <>
      {collections.map((collection) => (
        <SingleCollection key={collection.id} collection={collection} />
      ))}
    </>
  );
};

export default HomeCollections;
